import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Mail, Share2, MessageSquare, FileText,
  Copy, Check, Send, Download, ExternalLink, Sparkles,
  Twitter, Instagram, Facebook, Linkedin, ChevronRight,
  UserPlus, ClipboardList, BarChart3, Heart, Star, Gift,
  Trash2, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const API_URL = "https://api.recoverymatch.com";

const AdminBetaTesters = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [copiedItem, setCopiedItem] = useState(null);
  const [betaSignups, setBetaSignups] = useState([]);
  const [betaStats, setBetaStats] = useState({
    totalSignups: 0,
    invited: 0,
    converted: 0
  });
  const [loadingSignups, setLoadingSignups] = useState(false);

  // Beta signup landing page URL
  const betaLandingUrl = "https://www.recoverymatch.com/beta";
  const appStoreUrl = "https://apps.apple.com/app/id6757411835";

  // Fetch beta signups
  useEffect(() => {
    if (activeTab === "testers" || activeTab === "overview") {
      fetchBetaSignups();
    }
  }, [activeTab]);

  const fetchBetaSignups = async () => {
    setLoadingSignups(true);
    try {
      const token = localStorage.getItem("recoverymatch_admin_token") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/beta/signups?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setBetaSignups(data.signups || []);
      setBetaStats({
        totalSignups: data.total || 0,
        invited: data.invited || 0,
        converted: data.converted || 0
      });
    } catch (error) {
      console.error("Failed to fetch beta signups:", error);
    } finally {
      setLoadingSignups(false);
    }
  };

  const markAsInvited = async (signupId) => {
    try {
      const token = localStorage.getItem("recoverymatch_admin_token") || localStorage.getItem("token");
      await fetch(`${API_URL}/api/admin/beta/invite/${signupId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Marked as invited");
      fetchBetaSignups();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const deleteSignup = async (signupId) => {
    if (!confirm("Remove this signup?")) return;
    try {
      const token = localStorage.getItem("recoverymatch_admin_token") || localStorage.getItem("token");
      await fetch(`${API_URL}/api/admin/beta/signup/${signupId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Signup removed");
      fetchBetaSignups();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "landing", label: "Landing Page", icon: FileText },
    { id: "emails", label: "Email Templates", icon: Mail },
    { id: "social", label: "Social Media", icon: Share2 },
    { id: "feedback", label: "Feedback", icon: MessageSquare },
    { id: "testers", label: "Manage Testers", icon: Users },
  ];

  // Email Templates
  const emailTemplates = {
    invitation: {
      subject: "You're Invited to Beta Test RecoveryMatch! 💜",
      body: `Hi [NAME],

We're excited to invite you to be a beta tester for RecoveryMatch - the first dating app designed specifically for people in recovery!

As a beta tester, you'll get:
✨ Early access to the app before public launch
💜 Direct influence on features and improvements
🎁 FREE 3-month Recovery Elite subscription at launch
⭐ Exclusive "Beta Tester" badge on your profile

What we need from you:
• Test the app for 2-3 weeks
• Share honest feedback about your experience
• Report any bugs or issues you encounter
• Suggest features that would help the recovery community

Ready to join? Download the app here:
[APP_STORE_LINK]

Use this exclusive beta code when signing up: BETA2024

Thank you for helping us build something meaningful for the recovery community!

With gratitude,
The RecoveryMatch Team

P.S. Know someone else in recovery who might be interested? Feel free to forward this invitation!`
    },
    reminder: {
      subject: "How's your RecoveryMatch beta experience going? 🌟",
      body: `Hi [NAME],

It's been a week since you joined the RecoveryMatch beta, and we'd love to hear from you!

Your feedback is incredibly valuable in helping us create the best possible experience for the recovery community.

Quick questions:
• Have you been able to create your profile?
• Have you connected with any matches yet?
• Is there anything confusing or frustrating about the app?

Just reply to this email with your thoughts, or use the in-app feedback button.

As a thank you for your time, we're adding an extra month to your free Recovery Elite subscription!

Keep going - you're making a difference! 💜

The RecoveryMatch Team`
    },
    feedbackRequest: {
      subject: "Share Your RecoveryMatch Beta Feedback 📝",
      body: `Hi [NAME],

Thank you for being part of the RecoveryMatch beta! We're getting closer to our public launch, and your insights have been invaluable.

We'd love 5 minutes of your time to complete our beta feedback survey:
[SURVEY_LINK]

Your responses help us:
• Fix bugs before launch
• Prioritize new features
• Improve the user experience
• Better serve the recovery community

As a thank you, everyone who completes the survey will receive:
🎁 6 months FREE Premium membership
⭐ Exclusive "Beta Tester" badge
💜 Early access to new features

Survey link: [SURVEY_LINK]

Thank you for helping us build something special!

With gratitude,
The RecoveryMatch Team`
    }
  };

  // Social Media Posts
  const socialPosts = {
    twitter: [
      {
        id: "tw1",
        text: `🚀 We're looking for beta testers for RecoveryMatch - the first dating app built FOR the recovery community, BY the recovery community.

Get early access + FREE premium membership.

DM us or sign up: ${betaLandingUrl}

#Recovery #SoberDating #BetaTesters #RecoveryMatch`,
        charCount: 280
      },
      {
        id: "tw2", 
        text: `💜 Finding love in recovery shouldn't mean compromising your sobriety.

We're building RecoveryMatch - a dating app that understands your journey.

Now seeking beta testers! Early access + perks await.

Join us: ${betaLandingUrl}

#RecoveryCommunity #SoberLife`,
        charCount: 275
      },
      {
        id: "tw3",
        text: `One year sober? Five years? Just starting?

RecoveryMatch celebrates every milestone. 🎉

We're looking for beta testers who get it.

Sign up: ${betaLandingUrl}

#Recovery #Dating #BetaTest`,
        charCount: 210
      }
    ],
    instagram: [
      {
        id: "ig1",
        text: `💜 BIG NEWS: We're looking for beta testers!

RecoveryMatch is the dating app that finally understands what it means to be in recovery.

✨ No judgment
✨ No awkward explanations
✨ Just people who GET IT

As a beta tester, you'll get:
• Early access before public launch
• FREE 3-month Recovery Elite subscription
• Direct input on features
• Exclusive "Beta Tester" badge

Ready to help us build something meaningful?

Link in bio to sign up! 👆

#RecoveryMatch #SoberDating #RecoveryCommunity #BetaTesters #SoberLife #RecoveryJourney #DatingApp #FindingLove #Recovery #Sobriety #CleanAndSober #RecoveryIsPossible`,
        type: "post"
      },
      {
        id: "ig2",
        text: `STORY IDEAS:

Slide 1: "We're building something special..."
Slide 2: "A dating app for the recovery community"
Slide 3: "Where your sobriety is celebrated, not questioned"
Slide 4: "Now looking for beta testers!"
Slide 5: "Swipe up to join" (with link)

Use: Recovery-themed images, purple/gold colors, heart + sobriety chip imagery`,
        type: "story"
      }
    ],
    facebook: [
      {
        id: "fb1",
        text: `🎉 Exciting announcement!

We're looking for beta testers for RecoveryMatch - a dating app created specifically for people in recovery.

Why RecoveryMatch?
💜 Connect with people who understand your journey
💜 12-step compatibility matching
💜 "Day One" celebrations for sobriety milestones
💜 Safe, judgment-free environment

As a beta tester, you'll receive:
✅ Early access before public launch
✅ FREE 3-month Recovery Elite subscription
✅ Direct influence on app features
✅ Exclusive "Beta Tester" badge

We believe everyone deserves love, especially those brave enough to choose recovery. Help us build something meaningful for our community.

👉 Sign up here: ${betaLandingUrl}

Share this with anyone who might be interested! 💜`,
        type: "post"
      },
      {
        id: "fb2",
        text: `To share in Facebook Groups (Recovery-focused):

---

Hi everyone! 👋

I wanted to share something that might interest this community.

There's a new dating app being built specifically for people in recovery called RecoveryMatch. They're currently looking for beta testers.

The app focuses on:
- Matching based on recovery journey compatibility
- Celebrating sobriety milestones
- Creating a safe space for sober dating

Beta testers get free premium access and can help shape the app's features.

If you're interested: ${betaLandingUrl}

(Mods - please remove if not allowed!)`,
        type: "group"
      }
    ],
    linkedin: [
      {
        id: "li1",
        text: `I'm excited to share that we're launching the beta program for RecoveryMatch - a dating application designed specifically for the recovery community.

The Problem:
Traditional dating apps don't account for the unique needs of people in recovery. Explaining your sobriety journey to potential matches can be awkward and vulnerable.

Our Solution:
RecoveryMatch creates a safe space where recovery is celebrated, not explained. Our matching algorithm considers recovery program compatibility, sobriety milestones, and shared values.

We're seeking beta testers who:
• Are in recovery (any stage)
• Want to help shape product development
• Can provide honest, constructive feedback

Beta testers receive early access, free premium features, and direct input into our roadmap.

Interested? Visit: ${betaLandingUrl}

Know someone in recovery who might benefit? Please share this post.

#Recovery #Startup #BetaTesting #MentalHealth #ProductDevelopment #DatingApp`,
        type: "post"
      }
    ]
  };

  // Feedback Survey Questions
  const surveyQuestions = [
    { id: 1, question: "How easy was it to create your profile?", type: "rating" },
    { id: 2, question: "How would you rate the match quality?", type: "rating" },
    { id: 3, question: "Did you feel safe and comfortable using the app?", type: "rating" },
    { id: 4, question: "What feature did you find most valuable?", type: "text" },
    { id: 5, question: "What's missing that you wish the app had?", type: "text" },
    { id: 6, question: "Would you recommend RecoveryMatch to others in recovery?", type: "nps" },
    { id: 7, question: "Any bugs or issues you encountered?", type: "text" },
    { id: 8, question: "Additional comments or suggestions?", type: "text" },
  ];

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Sparkles className="w-8 h-8" />
                Beta Tester Hub
              </h1>
              <p className="text-purple-200 mt-1">Recruit, manage, and engage your beta community</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-purple-200 text-sm">Total Signups</p>
              <p className="text-3xl font-bold">{betaStats.totalSignups}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-purple-200 text-sm">Invited</p>
              <p className="text-3xl font-bold">{betaStats.invited}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-purple-200 text-sm">Converted</p>
              <p className="text-3xl font-bold">{betaStats.converted}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-purple-200 text-sm">Conversion Rate</p>
              <p className="text-3xl font-bold">{betaStats.totalSignups > 0 ? Math.round((betaStats.converted / betaStats.totalSignups) * 100) : 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[52px] z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-purple-100 text-purple-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>🚀 Beta Program Checklist</CardTitle>
                <CardDescription>Track your progress in launching the beta</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { task: "Set up beta landing page", done: true },
                    { task: "Create email templates", done: true },
                    { task: "Prepare social media posts", done: true },
                    { task: "Create feedback survey", done: true },
                    { task: "Identify 50 potential testers", done: false },
                    { task: "Send first batch of invitations", done: false },
                    { task: "Onboard first 10 testers", done: false },
                    { task: "Collect initial feedback", done: false },
                    { task: "Iterate based on feedback", done: false },
                    { task: "Expand to 50 testers", done: false },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        item.done ? "bg-green-500 text-white" : "bg-gray-200"
                      }`}>
                        {item.done ? <Check className="w-4 h-4" /> : <span className="text-gray-500 text-sm">{i + 1}</span>}
                      </div>
                      <span className={item.done ? "text-gray-500 line-through" : "text-gray-700"}>{item.task}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>📊 Key Metrics to Track</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Beta signup conversion rate</li>
                    <li>• Daily/weekly active users</li>
                    <li>• Profile completion rate</li>
                    <li>• Match acceptance rate</li>
                    <li>• Message response rate</li>
                    <li>• Feature usage analytics</li>
                    <li>• Bug reports submitted</li>
                    <li>• Net Promoter Score (NPS)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>🎯 Beta Goals</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Recruit 50 beta testers</li>
                    <li>• Achieve 70%+ daily active rate</li>
                    <li>• Get feedback from 80%+ of testers</li>
                    <li>• Fix all critical bugs</li>
                    <li>• Achieve NPS score of 50+</li>
                    <li>• Create 10+ successful matches</li>
                    <li>• Validate core value proposition</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Landing Page Tab */}
        {activeTab === "landing" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>🌐 Beta Landing Page</CardTitle>
                <CardDescription>Preview and share your beta signup page</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-8 text-white">
                  <div className="max-w-2xl mx-auto text-center">
                    <Heart className="w-16 h-16 mx-auto mb-4 text-purple-200" />
                    <h2 className="text-3xl font-bold mb-4">Join the RecoveryMatch Beta</h2>
                    <p className="text-purple-100 mb-6">
                      Be among the first to experience the dating app built specifically for the recovery community.
                    </p>
                    <div className="bg-white/10 rounded-lg p-6 mb-6">
                      <p className="font-semibold mb-3">Beta Testers Get:</p>
                      <ul className="text-left space-y-2 text-purple-100">
                        <li>✨ Early access before public launch</li>
                        <li>💜 FREE 3-month Recovery Elite subscription</li>
                        <li>⭐ Exclusive "Beta Tester" badge</li>
                        <li>🎁 Direct influence on app features</li>
                      </ul>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button className="bg-white text-purple-700 hover:bg-purple-50">
                        Download on App Store
                      </Button>
                      <Button variant="outline" className="border-white text-white hover:bg-white/10">
                        Learn More
                      </Button>
                    </div>
                    <p className="text-sm text-purple-200 mt-4">Use code BETA2024 when signing up</p>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <Input value={betaLandingUrl} readOnly className="flex-1" />
                  <Button onClick={() => copyToClipboard(betaLandingUrl, "landing")}>
                    {copiedItem === "landing" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === "emails" && (
          <div className="space-y-6">
            {Object.entries(emailTemplates).map(([key, template]) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-600" />
                    {key === "invitation" && "Invitation Email"}
                    {key === "reminder" && "Reminder Email"}
                    {key === "feedbackRequest" && "Feedback Request Email"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Subject Line</label>
                      <div className="flex gap-2 mt-1">
                        <Input value={template.subject} readOnly className="flex-1" />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(template.subject, `${key}-subject`)}
                        >
                          {copiedItem === `${key}-subject` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email Body</label>
                      <div className="mt-1 relative">
                        <Textarea 
                          value={template.body} 
                          readOnly 
                          className="min-h-[300px] font-mono text-sm"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(template.body, `${key}-body`)}
                        >
                          {copiedItem === `${key}-body` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Social Media Tab */}
        {activeTab === "social" && (
          <div className="space-y-6">
            {/* Twitter */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Twitter className="w-5 h-5 text-blue-400" />
                  Twitter/X Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialPosts.twitter.map((post) => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4">
                    <p className="whitespace-pre-wrap text-gray-700 mb-3">{post.text}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{post.charCount} characters</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(post.text, post.id)}
                      >
                        {copiedItem === post.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                        Copy
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Instagram */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Instagram className="w-5 h-5 text-pink-500" />
                  Instagram Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialPosts.instagram.map((post) => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4">
                    <span className="inline-block px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded mb-2">
                      {post.type === "post" ? "Feed Post" : "Story Ideas"}
                    </span>
                    <p className="whitespace-pre-wrap text-gray-700 mb-3">{post.text}</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(post.text, post.id)}
                    >
                      {copiedItem === post.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      Copy
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Facebook */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  Facebook Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialPosts.facebook.map((post) => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded mb-2">
                      {post.type === "post" ? "Page Post" : "Group Post"}
                    </span>
                    <p className="whitespace-pre-wrap text-gray-700 mb-3">{post.text}</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(post.text, post.id)}
                    >
                      {copiedItem === post.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      Copy
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* LinkedIn */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Linkedin className="w-5 h-5 text-blue-700" />
                  LinkedIn Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialPosts.linkedin.map((post) => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4">
                    <p className="whitespace-pre-wrap text-gray-700 mb-3">{post.text}</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(post.text, post.id)}
                    >
                      {copiedItem === post.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      Copy
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === "feedback" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>📝 Beta Feedback Survey</CardTitle>
                <CardDescription>Questions to ask your beta testers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {surveyQuestions.map((q) => (
                    <div key={q.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="text-sm text-purple-600 font-medium">Q{q.id}.</span>
                          <p className="text-gray-700 mt-1">{q.question}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          q.type === "rating" ? "bg-yellow-100 text-yellow-700" :
                          q.type === "nps" ? "bg-green-100 text-green-700" :
                          "bg-blue-100 text-blue-700"
                        }`}>
                          {q.type === "rating" ? "1-5 Rating" : 
                           q.type === "nps" ? "0-10 NPS" : "Text"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-700">
                    <strong>Tip:</strong> Create this survey using Google Forms, Typeform, or SurveyMonkey. 
                    Add the link to your feedback request email template.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>💬 In-App Feedback</CardTitle>
                <CardDescription>Feedback submitted through the app</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No feedback received yet</p>
                  <p className="text-sm">Feedback from beta testers will appear here</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Manage Testers Tab */}
        {activeTab === "testers" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>👥 Beta Signups ({betaStats.totalSignups})</span>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => copyToClipboard(betaLandingUrl, "signup-link")}>
                      {copiedItem === "signup-link" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      Copy Signup Link
                    </Button>
                    <Button variant="outline" onClick={fetchBetaSignups}>
                      <Download className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>People who signed up at {betaLandingUrl}</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSignups ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : betaSignups.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No beta signups yet</p>
                    <p className="text-sm mt-1">Share your signup link to get beta testers!</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        copyToClipboard(betaLandingUrl, "empty-link");
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Signup Link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {betaSignups.map((signup) => (
                      <div 
                        key={signup.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          signup.invited ? "bg-green-50 border-green-200" : "bg-white border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            signup.invited ? "bg-green-100 text-green-600" : "bg-purple-100 text-purple-600"
                          }`}>
                            {signup.invited ? <CheckCircle className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{signup.name}</p>
                            <p className="text-sm text-gray-500">{signup.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">
                            {new Date(signup.signed_up_at).toLocaleDateString()}
                          </span>
                          {signup.invited ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              Invited
                            </span>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => markAsInvited(signup.id)}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Mark Invited
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteSignup(signup.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>🎁 Beta Rewards</CardTitle>
                <CardDescription>Incentives for your beta testers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                    <Gift className="w-8 h-8 mb-3" />
                    <h3 className="font-bold">Early Access</h3>
                    <p className="text-purple-100 text-sm mt-1">First to try new features</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl p-6 text-white">
                    <Star className="w-8 h-8 mb-3" />
                    <h3 className="font-bold">Beta Tester Badge</h3>
                    <p className="text-yellow-100 text-sm mt-1">Exclusive profile badge</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                    <Heart className="w-8 h-8 mb-3" />
                    <h3 className="font-bold">1 Month Recovery+</h3>
                    <p className="text-green-100 text-sm mt-1">Free premium at launch</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminBetaTesters;
